/**
 * InspectionDueWidget
 * Visar fordon med kommande besiktning (BaseDashboardWidget + tilldelning).
 */

import React, { useState, useMemo } from 'react';
import { useTranslation, useLanguage } from '@/store/languageStore';
import { uiDateLocale } from '@/utils/uiLocale';
import { ClipboardDocumentCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { BaseDashboardWidget } from './BaseDashboardWidget';
import VehicleRegistrationLink from './VehicleRegistrationLink';

interface InspectionVehicle {
  vehicleId: number;
  registrationNumber: string;
  make?: string;
  model?: string;
  organizationName?: string;
  inspectionDate: string;
  daysUntil: number;
  isOverdue: boolean;
}

interface InspectionMeta {
  totalCount: number;
  overdueCount: number;
  daysAhead: number;
}

const InspectionDueWidget: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [meta, setMeta] = useState<InspectionMeta | null>(null);

  const vehicleAssignments = useMemo(
    () => ({
      categoryCode: 'INSPECTION',
      getVehicleId: (v: InspectionVehicle) => v.vehicleId,
      getRegistrationNumber: (v: InspectionVehicle) => v.registrationNumber,
      buildAssignmentTitle: (v: InspectionVehicle) =>
        `${t('inspectionWidget.titleWithDays')}: ${v.registrationNumber}`,
    }),
    [t],
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(uiDateLocale(language), {
      day: 'numeric',
      month: 'short',
    }).format(date);
  };

  const getDaysLabel = (days: number) => {
    if (days < 0) {
      const absDays = Math.abs(days);
      return t('inspectionWidget.daysAgo').replace('{days}', String(absDays));
    }
    if (days === 0) return t('inspectionWidget.today');
    return `${days}d`;
  };

  return (
    <BaseDashboardWidget<InspectionVehicle>
      icon={ClipboardDocumentCheckIcon}
      colorScheme="orange"
      title={t('inspectionWidget.titleWithDays')}
      subtitle={t('inspectionWidget.titleWithDays')}
      apiEndpoint="/api/dashboard/data/upcoming-inspections"
      apiParams={{ days: 40 }}
      emptyMessage={t('inspectionWidget.noVehicles')}
      getItemKey={(vehicle) => vehicle.vehicleId}
      vehicleAssignments={vehicleAssignments}
      onDataLoaded={(_, metaData) => setMeta(metaData)}
      renderBadge={() => {
        const overdueCount = meta?.overdueCount || 0;
        if (overdueCount === 0) return null;

        return (
          <span className="flex items-center gap-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium px-2 py-1 rounded-full">
            <ExclamationTriangleIcon className="w-3 h-3" />
            {overdueCount} {t('inspectionWidget.overdue')}
          </span>
        );
      }}
      renderItem={(vehicle) => (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <VehicleRegistrationLink
              registrationNumber={vehicle.registrationNumber}
              vehicleId={vehicle.vehicleId}
              className="font-medium"
            />
            {vehicle.make && (
              <span className="text-xs text-textTertiary">{vehicle.make}</span>
            )}
          </div>
          <div className="text-xs">
            <span className={
              vehicle.isOverdue
                ? 'text-red-500'
                : vehicle.daysUntil <= 14
                  ? 'text-orange-500'
                  : 'text-textTertiary'
            }
            >
              {formatDate(vehicle.inspectionDate)} ({getDaysLabel(vehicle.daysUntil)})
            </span>
          </div>
        </div>
      )}
    />
  );
};

export default InspectionDueWidget;
